import instanceJWT from "../../api/apiJwt";
import { responsibleAddInfo } from "../../common/helpers/eplex/responsibleProfile";
import { useClientContextStore } from "./eplexClientContext";

interface CartTabState {
  requestId: string;
  cartQuantity: number;
  addedItems: Record<number, number>;
  results: Record<string, unknown>[];
  reorderId?: string | null;
}

export function createCartActions(searches: Record<number, CartTabState>, ensureTabState: (tabId: number) => void) {
  const fetchItemInfo = async (
    tabId: number,
    rowData: Record<string, unknown>,
    tm = 0,
  ): Promise<Record<string, unknown> | undefined> => {
    ensureTabState(tabId);
    const requestBody = {
      ext_info: {
        agr_guid: "",
        agr_name: "",
        cp_guid: "00000000-0000-0000-0000-000000000000",
        cp_name: "",
        tm,
        wh_guid: "",
        wh_name: "",
      },
      item: {
        article: rowData.article ?? "",
        brand: rowData.brand ?? "",
        brand_id: rowData.brand_id ?? "",
        cat: rowData.cat ?? "",
        chance: rowData.chance ?? "",
        city: rowData.city ?? "",
        cost: rowData.cost ?? 0,
        cost1c: 0,
        count: rowData.count ?? "",
        img: rowData.img ?? "",
        mcount: 0,
        multiplicity: 0,
        name: rowData.name ?? "",
        pfn: rowData.pfn ?? "",
        stock: rowData.stock ?? "",
        tcost: 0,
        time: rowData.time ?? "",
        time_int: rowData.time_int ?? 0,
        time_orig: rowData.time_orig ?? "",
      },
      plugin_id: isNaN(Number(rowData.plugin_id)) ? 0 : Number(rowData.plugin_id),
      pos_idx: rowData.pos_idx ?? 0,
      tab_id: null,
    };
    const url = `eplex/search/result/${searches[tabId].requestId}/info`;
    try {
      const response = await instanceJWT.post(url, requestBody);
      return response.data.data;
    } catch (error) {
      console.error("Fetching item info error: ", error);
    }
  };

  const addToCart = async (
    tabId: number,
    pluginId: number,
    posIndex: number,
    basketQuantity: number,
    comment: string,
    tcost = 0,
    addInfo?: Record<string, unknown>,
  ): Promise<Record<string, unknown> | undefined> => {
    ensureTabState(tabId);
    // При перепроценке из уведомления об отказе вкладка помнит id исходного заказа —
    // прокидываем его в позицию как reorder_id, чтобы бэкенд связал старый и новый заказ.
    const reorderId = searches[tabId].reorderId;
    const item: Record<string, unknown> = {
      sreqid: searches[tabId].requestId,
      plugin_id: pluginId,
      pos_idx: posIndex,
      qty: basketQuantity,
      comment,
      tcost,
    };
    if (reorderId) {
      item.reorder_id = reorderId;
    }
    // Свободные метаданные позиции (например, название подбора) — читаются в журнале.
    // Ответственного дописываем здесь, а не в вызывающих: точка добавления в
    // корзину одна, и ключ не потеряется при появлении нового способа добавить
    // позицию. Заказ у строки появится позже, а колонка «Ответственный» нужна
    // сразу — см. RESPONSIBLE_PROFILE_ID_ADD_INFO_KEY (AUT-235).
    const clientContext = useClientContextStore();
    const mergedAddInfo = {
      ...addInfo,
      ...responsibleAddInfo(clientContext.profileId, clientContext.profileName),
    };
    if (Object.keys(mergedAddInfo).length > 0) {
      item.add_info = mergedAddInfo;
    }
    const requestBody = {
      confirmed: true,
      items: [item],
    };
    try {
      const response = await instanceJWT.put("eplex/temp_cart", requestBody);
      return response.data.data;
    } catch (error) {
      console.error("Adding to cart error: ", error);
    }
  };

  const incrementCartQuantity = (tabId: number, quantity: number) => {
    if (searches[tabId]) {
      searches[tabId].cartQuantity = (searches[tabId].cartQuantity ?? 0) + quantity;
    }
  };

  const incrementAddedItem = (tabId: number, rowId: number, quantity: number) => {
    const tab = searches[tabId];
    if (!tab) return;
    tab.addedItems = {
      ...tab.addedItems,
      [rowId]: (tab.addedItems[rowId] ?? 0) + quantity,
    };
    if (Array.isArray(tab.results)) {
      const idx = tab.results.findIndex((item) => item.id === rowId);
      if (idx !== -1) {
        tab.results[idx] = { ...tab.results[idx], count: Math.max(0, Number(tab.results[idx].count) - quantity) };
      }
    }
  };

  const decrementItemCount = (tabId: number, rowId: number, quantity: number) => {
    const tab = searches[tabId];
    if (!tab || !Array.isArray(tab.results)) return;
    const idx = tab.results.findIndex((item) => item.id === rowId);
    if (idx !== -1) {
      tab.results[idx] = { ...tab.results[idx], count: Math.max(0, Number(tab.results[idx].count) - quantity) };
    }
  };

  const decrementCartQuantity = (tabId: number, quantity: number) => {
    if (searches[tabId]) {
      searches[tabId].cartQuantity = Math.max(0, (searches[tabId].cartQuantity ?? 0) - quantity);
    }
  };

  const removeAddedItem = (tabId: number, rowId: number) => {
    const tab = searches[tabId];
    if (!tab) return;
    const newAddedItems = { ...tab.addedItems };
    delete newAddedItems[rowId];
    tab.addedItems = newAddedItems;
  };

  const incrementItemCount = (tabId: number, rowId: number, quantity: number) => {
    const tab = searches[tabId];
    if (!tab || !Array.isArray(tab.results)) return;
    const idx = tab.results.findIndex((item) => item.id === rowId);
    if (idx !== -1) {
      tab.results[idx] = {
        ...tab.results[idx],
        count: Math.max(0, Number(tab.results[idx].count) + quantity),
      };
    }
  };

  return {
    fetchItemInfo,
    addToCart,
    incrementCartQuantity,
    incrementAddedItem,
    decrementItemCount,
    decrementCartQuantity,
    removeAddedItem,
    incrementItemCount,
  };
}
